import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
  Account,
  AccountDomainSetting,
  Dictionary,
  Section,
  SectionDomain,
  SectionDomainSetting,
} from '../../models';
import {
  getAccounts,
  getDomainSettings,
  getSections,
  getSelectedAccountDomainSetting,
  getSelectedSectionsDomains,
  State,
} from '../../store/reducers';

@Injectable({
  providedIn: 'root',
})
export class PrivateDomainPresenter {
  constructor(private readonly store: Store<State>) {}

  getAccounts(): Observable<Account[]> {
    return combineLatest([
      this.store.pipe(select(getAccounts)),
      this.store.pipe(select(getDomainSettings)),
    ]).pipe(
      filter(([accounts, _]) => !!accounts),
      map(([accounts, domainSettings]) => {
        if (!domainSettings || !domainSettings.length) {
          return accounts;
        }
        const lookup = {};
        for (const d of domainSettings) {
          lookup[d.accountId] = true;
        }
        return accounts.filter((a) => !lookup[a.id]);
      }),
    );
  }

  getSections(): Observable<SectionDomain[]> {
    return combineLatest([
      this.store.pipe(select(getSections)),
      this.store.pipe(select(getSelectedSectionsDomains)),
    ]).pipe(
      filter(([sections, _]) => !!sections),
      map(([sections, selectedSectionsDomains]) =>
        this.getFilteredSections(sections, selectedSectionsDomains),
      ),
    );
  }

  enableSave() {
    return this.store.pipe(select(getSelectedAccountDomainSetting)).pipe(
      filter(
        (accountDomainSetting) =>
          !!accountDomainSetting && !!accountDomainSetting.domainNames,
      ),
      map((accountDomainSetting) =>
        this.validateDomainNames(accountDomainSetting),
      ),
    );
  }

  private isSpace(text: string) {
    return !!text.match(/\s/g);
  }

  private isAccountDomainValid(name: string) {
    return !!name && !!name.length;
  }

  private validateDomainNames(accountDomainSetting: AccountDomainSetting) {
    let enableSave = this.isAccountDomainValid(
      accountDomainSetting.domainNames.accountDomain,
    );

    if (
      enableSave &&
      this.isSpace(accountDomainSetting.domainNames.accountDomain)
    ) {
      return false;
    }
    const { sections } = accountDomainSetting.domainNames;
    if (sections && Object.keys(sections)) {
      for (const sectionData of Object.values(sections)) {
        enableSave =
          !!sectionData.domainName && !!sectionData.domainName.length;
        if (enableSave) {
          break;
        }

        if (enableSave) {
          break;
        }
      }
    }
    return enableSave;
  }

  private getFilteredSections(
    sections: Dictionary<Section>,
    selectedSectionDomain: SectionDomainSetting[],
  ) {
    const lookup = {};
    if (selectedSectionDomain) {
      for (const s of selectedSectionDomain) {
        lookup[s.id] = true;
      }
    }

    if (!sections || !Object.keys(sections).length) {
      return [];
    }
    return Object.entries(sections).reduce((a, [key, value]) => {
      if (!lookup[key]) {
        a.push({ id: key, name: value.name });
      }
      return a;
    }, []);
  }
}
